<template>
	<section
		v-if="showSection"
		class="teacher-recommend-section"
		:aria-label="t(`${I18N_NS}.teacherRecommend.ariaLabel`)"
		:aria-busy="showSkeleton"
	>
		<header class="teacher-recommend-section__header">
			<h2 class="teacher-recommend-section__title">{{ t(`${I18N_NS}.teacherRecommend.title`) }}</h2>
			<div class="teacher-recommend-section__filters">
				<div class="sort-select-wrapper">
					<select v-model="themeValue" class="sort-select" @change="handleThemeChange">
						<option v-for="option in themeOptions" :key="option.value" :value="option.value">
							{{ option.label }}
						</option>
					</select>
					<img class="sort-arrow" src="../../images/expand.png" alt="" />
				</div>
				<div class="sort-select-wrapper">
					<select v-model="sortValue" class="sort-select" @change="handleSortChange">
						<option v-for="option in sortOptions" :key="option.value" :value="option.value">
							{{ option.label }}
						</option>
					</select>
					<img class="sort-arrow" src="../../images/expand.png" alt="" />
				</div>
			</div>
		</header>

		<TeacherCardsSkeleton v-if="showSkeleton" variant="grid" :show-title="false" :count="8" />

		<template v-else>
			<div class="teacher-recommend-section__list">
				<article
					v-for="teacher in teacherList"
					:key="getTeacherUid(teacher)"
					:ref="el => setCardRef(el, teacher)"
					class="teacher-recommend-card"
					@click="handleCardClick(teacher)"
				>
					<div class="teacher-recommend-card__image-box">
						<img
							class="teacher-recommend-card__avatar"
							:src="teacher?.avatar"
							:alt="teacher?.name || t(`${I18N_NS}.teacherResource.avatarAlt`)"
						/>
					</div>

					<div class="teacher-recommend-card__content">
						<h3 class="teacher-recommend-card__name">{{ teacher?.nickname }}</h3>
						<p class="teacher-recommend-card__title">{{ teacher?.displayTitle }}</p>

						<div class="teacher-recommend-card__meta">
							<img :src="ratingStar" alt="" aria-hidden="true" />
							<span>{{ teacher?.displayScore }}({{ teacher?.displayConsultCount }})</span>
						</div>

						<div class="teacher-recommend-card__price">
							<img :src="priceIcon" alt="" aria-hidden="true" />
							<span>{{ teacher?.displayPrice }}</span>
							<span class="teacher-recommend-card__price-suffix">{{
								t(`${I18N_NS}.teacherRecommend.priceFrom`)
							}}</span>
						</div>

						<button
							class="teacher-recommend-card__button"
							type="button"
							@click.stop="handleCardAction(teacher)"
						>
							{{ teacher?.actionText }}
						</button>
					</div>
				</article>
			</div>

			<div v-if="hasMore" class="teacher-recommend-section__footer">
				<button class="teacher-recommend-section__more" type="button" @click="loadMore">
					{{ t(`${I18N_NS}.teacherRecommend.loadMore`) }}
				</button>
			</div>
		</template>
	</section>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { getHighServiceOriginTeacher } from "~/api/api";
import { logEvent } from "~/utils/firebase";
import { getChannel } from "~/utils/getChannel";
import ratingStar from "../../../images/rating-star.png";
import priceIcon from "../../../images/logo_icon.png";
import { useI18n } from "~/composables/useI18n";
import TeacherCardsSkeleton from "../teacher-cards-skeleton/index.vue";
import {
	getMentorTypeByAreaType,
	getPriceBucket,
	normalizeMentorRating,
	useMentorListTracking,
} from "@variants/seer/master-fortune/composables/useMentorListTracking";

const I18N_NS = "masterFortune";

const RESOURCE_SIZE = 10;
const DEFAULT_PAGE = 1;
const DEFAULT_PRICE = "495";
const TEACHER_DETAIL_AREA_TYPE = 10;
const TEACHER_UID_STORAGE_KEY = "pending_chat_teacher_uid";
const STORAGE_KEY = "master_fortune_pc_teacher_recommend_state";

const { t, trackTranslationDeps } = useI18n();
const { track } = useMentorListTracking({
	deviceType: "pc",
	mentorType: "normal",
});

const sortValue = ref("order_count");
const previousSortValue = ref("order_count");
const sortOptions = computed(() => {
	trackTranslationDeps();
	return [
		{ label: t(`${I18N_NS}.teacherRecommend.sortOptions.order_count`), value: "order_count" },
		{ label: t(`${I18N_NS}.teacherRecommend.sortOptions.buy_total`), value: "buy_total" },
	];
});
const handleSortChange = event => {
	event.target.blur();
	fetchTeacherPage(DEFAULT_PAGE);
	track("mentor_sort_click", {
		sort_type: sortValue.value,
		previous_sort_type: previousSortValue.value,
		result_count: teacherList.value.length,
	});
	previousSortValue.value = sortValue.value;
};

const themeValue = ref(1);
const themeOptions = computed(() => {
	trackTranslationDeps();
	return [
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.5`), value: 5 },
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.4`), value: 4 },
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.3`), value: 3 },
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.2`), value: 2 },
		{ label: t(`${I18N_NS}.teacherRecommend.themeOptions.1`), value: 1 },
	];
});
const handleThemeChange = event => {
	event.target.blur();
	fetchTeacherPage(DEFAULT_PAGE);
	track("mentor_filter_click", {
		filter_type: "theme",
		filter_value: themeValue.value,
		result_count: teacherList.value.length,
	});
};

const openLoginPopup = inject("openLoginPopup", null);

const rawTeacherListData = ref([]);
const currentPage = ref(DEFAULT_PAGE);
const lastPage = ref(DEFAULT_PAGE);
const loading = ref(true);
const cardRefs = new Map();
const impressedMentorIds = new Set();
let cardObserver = null;

const teacherList = computed(() => {
	trackTranslationDeps();
	return (rawTeacherListData.value || []).map(formatTeacherResource);
});
const hasMore = computed(() => currentPage.value < lastPage.value);
const showSkeleton = computed(() => loading.value && !teacherList.value.length);
const showSection = computed(() => loading.value || teacherList.value.length > 0);

function isDetailOnlyTeacher(teacher) {
	return Number(teacher?.area_type) === TEACHER_DETAIL_AREA_TYPE;
}

function formatTeacherResource(teacher) {
	const scoreText = formatScore(teacher?.score);
	const consultCount = formatConsultCount(teacher?.answers || teacher?.evaluate_num);
	const displayTitle = normalizeTitle(teacher);
	const displayPrice = formatPrice(
		teacher?.final_price ||
			teacher?.price ||
			teacher?.discount_price ||
			teacher?.unit_price ||
			teacher?.min_price
	);
	const detailOnly = isDetailOnlyTeacher(teacher);

	return {
		...teacher,
		teacher_uid: getTeacherUid(teacher),
		name: teacher?.nickname || teacher?.name || teacher?.nickname_en || "",
		displayScore: scoreText,
		displayConsultCount: consultCount,
		displayTitle,
		displayPrice,
		actionText: detailOnly
			? t(`${I18N_NS}.teacherRecommend.viewDetail`)
			: t(`${I18N_NS}.teacherRecommend.consultNow`),
	};
}

function getTeacherUid(teacher) {
	return teacher?.teacher_uid || teacher?.answer_id || teacher?.id || "";
}

function normalizeTitle(teacher) {
	return teacher?.tags;
}

function formatScore(value) {
	const score = Number(value);

	if (!score) {
		return "5.0";
	}

	return Math.min(score, 5).toFixed(1);
}

function formatConsultCount(value) {
	const count = Number(value) || 0;
	return t(`${I18N_NS}.teacherRecommend.consultCount`, { count });
}

function formatPrice(value) {
	if (value === undefined || value === null || value === "") {
		return DEFAULT_PRICE;
	}

	if (typeof value === "number") {
		return String(value);
	}

	const priceText = String(value).replace(/\s+/g, "").trim();

	if (!priceText) {
		return DEFAULT_PRICE;
	}

	return priceText.replace(/起$/, "");
}

async function fetchTeacherPage(page) {
	loading.value = true;
	try {
		const res = await getHighServiceOriginTeacher({
			page,
			size: RESOURCE_SIZE,
			v: 2,
			sort_type: sortValue.value,
			search_type: themeValue.value,
		});
		const list = res?.data?.list || [];
		const pager = res?.data?.pager || {};

		if (page === DEFAULT_PAGE) {
			rawTeacherListData.value = list;
		} else {
			rawTeacherListData.value = [...rawTeacherListData.value, ...list];
		}

		currentPage.value = Number(pager?.current_page || page);
		lastPage.value = Number(pager?.last_page || page);
		persistState();
	} catch (error) {
		console.error("getHighServiceOriginTeacher error:", error);
	} finally {
		loading.value = false;
	}
}

async function loadMore() {
	if (!hasMore.value || loading.value) return;
	track("mentor_load_more_click", {
		page_index: currentPage.value + 1,
		result_count: teacherList.value.length,
		filter_type: "theme",
		sort_type: sortValue.value,
	});
	await fetchTeacherPage(currentPage.value + 1);
}

function handleCardAction(teacher) {
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;

	if (isDetailOnlyTeacher(teacher)) {
		toTeacherDetail(teacherUid);
		return;
	}

	track("mentor_consult_click", {
		mentor_id: teacherUid,
		mentor_type: getMentorTypeByAreaType(teacher?.area_type),
		section_id: "hot_mentor",
		price_bucket: getPriceBucket(teacher?.displayPrice),
	});
	toChat(teacherUid);
}

function handleCardClick(teacher) {
	const teacherUid = getTeacherUid(teacher);
	if (!teacherUid) return;
	track("mentor_card_click", {
		mentor_id: teacherUid,
		mentor_type: getMentorTypeByAreaType(teacher?.area_type),
		position: teacherList.value.findIndex(item => getTeacherUid(item) === teacherUid) + 1,
		topic_tags: teacher?.displayTitle || "",
	});
	toTeacherDetail(teacherUid);
}

function toTeacherDetail(teacherUid) {
	if (!teacherUid) return;
	navigateTo(`/seer/teacher-detail/${teacherUid}`);
}

function toChat(teacherUid) {
	if (!teacherUid) return;

	if (import.meta.client) {
		logEvent("gw_to_free_chat_click", {
			channel: getChannel(),
		});

		if (!localStorage.getItem("token")) {
			localStorage.setItem(TEACHER_UID_STORAGE_KEY, teacherUid);
		}
	}

	const targetUrl = `/seer/chat/${teacherUid}`;

	if (!openLoginPopup) {
		navigateTo(targetUrl);
		return;
	}

	openLoginPopup(() => {
		if (import.meta.client) {
			localStorage.removeItem(TEACHER_UID_STORAGE_KEY);
		}
		navigateTo(targetUrl);
	}, false);

	if (import.meta.client && localStorage.getItem("token")) {
		navigateTo(targetUrl);
	}
}

const persistState = () => {
	if (!import.meta.client) return;

	sessionStorage.setItem(
		STORAGE_KEY,
		JSON.stringify({
			sortValue: sortValue.value,
			themeValue: themeValue.value,
			currentPage: currentPage.value,
			lastPage: lastPage.value,
			teacherListData: rawTeacherListData.value,
		})
	);
};

const restoreState = () => {
	if (!import.meta.client) return false;

	const raw = sessionStorage.getItem(STORAGE_KEY);
	if (!raw) return false;

	try {
		const data = JSON.parse(raw);
		sortValue.value = data?.sortValue || "order_count";
		themeValue.value = Number(data?.themeValue || 1);
		currentPage.value = Number(data?.currentPage || DEFAULT_PAGE);
		lastPage.value = Number(data?.lastPage || DEFAULT_PAGE);
		rawTeacherListData.value = Array.isArray(data?.teacherListData) ? data.teacherListData : [];
		return rawTeacherListData.value.length > 0;
	} catch (error) {
		console.warn("恢复导师推荐状态失败:", error);
		return false;
	}
};

const setCardRef = (el, teacher) => {
	const id = getTeacherUid(teacher);
	if (!id) return;
	if (el) cardRefs.set(id, el);
	else cardRefs.delete(id);
};

function refreshCardObserver() {
	if (!import.meta.client) return;
	cardObserver?.disconnect();
	cardObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const mentorId = entry.target.dataset.mentorId;
				if (!mentorId || impressedMentorIds.has(mentorId)) return;
				if (!entry.isIntersecting) return;
				const teacher = teacherList.value.find(item => getTeacherUid(item) === mentorId);
				if (!teacher) return;
				impressedMentorIds.add(mentorId);
				track("mentor_card_impression", {
					mentor_id: mentorId,
					mentor_type: getMentorTypeByAreaType(teacher?.area_type),
					position: teacherList.value.findIndex(item => getTeacherUid(item) === mentorId) + 1,
					topic_tags: teacher?.displayTitle || "",
					price_bucket: getPriceBucket(teacher?.displayPrice),
					rating: normalizeMentorRating(teacher?.displayScore),
				});
			});
		},
		{ threshold: [0.3] }
	);

	teacherList.value.forEach(teacher => {
		const mentorId = getTeacherUid(teacher);
		const el = cardRefs.get(mentorId);
		if (!el) return;
		el.dataset.mentorId = mentorId;
		cardObserver.observe(el);
	});
}

watch(
	() => teacherList.value.length,
	() => nextTick(refreshCardObserver)
);

onMounted(async () => {
	if (!restoreState()) {
		await fetchTeacherPage(DEFAULT_PAGE);
	} else {
		loading.value = false;
	}
	nextTick(refreshCardObserver);
});

onBeforeUnmount(() => {
	cardObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.teacher-recommend-section {
	width: 100%;
	min-width: 0;
	max-width: 100%;

	border-radius: 20px;
	padding: 30px;
	box-shadow: 0 4px 24.6px 0 rgba(0, 0, 0, 0.1);

	&__filters {
		display: flex;
		align-items: center;
		gap: 32px;
	}

	&__header {
		margin-bottom: 30px;
		display: flex;
		justify-content: space-between;
		align-items: center;

		.sort-select-wrapper {
			position: relative;
			display: flex;
			align-items: center;

			.sort-select {
				box-sizing: border-box;
				padding: 0 36px 0 16px;
				background: #fff;
				border: 1px solid #efefef;
				border-radius: 8px;
				font-size: 16px;
				line-height: 36px;
				color: #595959;
				cursor: pointer;
				appearance: none;
				-webkit-appearance: none;
				-moz-appearance: none;
				min-width: 140px;
				height: 42px;
			}

			.sort-select:focus + .sort-arrow {
				transform: translateY(-50%) rotate(180deg);
			}

			.sort-arrow {
				position: absolute;
				right: 12px;
				top: 50%;
				width: 12px;
				height: 12px;
				pointer-events: none;
				transition: transform 0.3s ease;
				transform: translateY(-50%);
			}
		}
	}

	&__title {
		margin: 0;
		font-size: 28px;
		line-height: 60px;
		color: #333;
	}

	&__list {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 40px;
		width: 100%;
		min-width: 0;
	}

	&__footer {
		display: flex;
		justify-content: center;
		margin-top: 40px;
	}

	&__more {
		min-width: 129px;
		height: 38px;
		padding: 0 18px;
		border: 1px solid #efefef;
		border-radius: 6px;
		background: #ffffff;
		font-weight: 400;
		font-size: 16px;
		line-height: 26px;
		color: #595959;

		&:hover {
			background: #e5e5e5;
		}
	}
}

.teacher-recommend-card {
	display: flex;
	flex-direction: column;
	width: 100%;
	background: transparent;
	border: none;
	border-radius: 0;
	overflow: visible;
	cursor: pointer;
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;

	&:hover {
		transform: translateY(-4px);

		.teacher-recommend-card__content {
			box-shadow: 0 18px 42px rgba(43, 47, 54, 0.1);
		}
	}

	&__image-box {
		display: flex;
		align-items: flex-end;
		justify-content: center;
		height: 242px;
		padding: 0 20px;
		background: #fffaea;
		border-radius: 16px 16px 0 0;
		overflow: hidden;
	}

	&__avatar {
		display: block;
		width: 100%;
		max-width: 232px;
		height: auto;
		object-fit: contain;
	}

	&__content {
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: 16px 18px 18px;
		background: #ffffff;
		border: 1px solid #efefef;
		border-top: none;
		border-radius: 0 0 16px 16px;
		transition: box-shadow 0.2s ease;
	}

	&__name {
		margin: 0;
		display: block;
		width: 100%;
		font-weight: 500;
		font-size: 24px;
		line-height: 1;
		height: 24px;
		color: #333333;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&__title {
		margin: 12px 0 0;
		display: block;
		width: 100%;
		font-size: 16px;
		line-height: 16px;
		height: 16px;
		color: #666666;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	&__meta {
		display: inline-flex;
		align-items: center;
		width: fit-content;
		max-width: 100%;
		gap: 2px;
		margin-top: 12px;
		padding: 5px;
		background: #fdfae9;
		border-radius: 2px;
		font-size: 14px;
		line-height: 16px;
		color: #595959;
		overflow: hidden;

		img {
			width: 14px;
			height: 14px;
			flex-shrink: 0;
		}

		span {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	&__price {
		display: inline-flex;
		align-items: center;
		gap: 3px;
		margin-top: 12px;
		margin-bottom: 12px;
		font-weight: 700;
		font-size: 20px;
		line-height: 30px;
		color: #de2828;

		img {
			width: 14px;
			height: 14px;
			flex-shrink: 0;
		}
	}

	&__price-suffix {
		font-size: 12px;
		margin-top: 2px;
		font-weight: 500;
		line-height: 1;
		align-self: center;
	}

	&__button {
		width: 100%;
		height: 48px;
		margin-top: auto;
		border: 0;
		border-radius: 8px;
		background: #333333;
		font-weight: 500;
		font-size: 20px;
		line-height: 48px;
		color: #ffffff;
		cursor: pointer;
		transition:
			opacity 0.2s ease,
			transform 0.2s ease;

		&:hover {
			opacity: 0.7;
			transform: translateY(-1px);
		}
	}
}

@media (max-width: 1200px) {
	.teacher-recommend-section {
		&__list {
			grid-template-columns: repeat(4, minmax(0, 1fr));
		}
	}
}

@media (max-width: 1024px) {
	.teacher-recommend-section {
		&__header {
			margin: 28px 0;
		}

		&__title {
			font-size: 30px;
			line-height: 42px;
		}

		&__list {
			grid-template-columns: repeat(3, minmax(0, 1fr));
			gap: 16px;
		}
	}
}

@media (max-width: 900px) {
	.teacher-recommend-section {
		width: 100%;
		min-width: 0;
		max-width: 100%;
		overflow-x: clip;

		&__header {
			margin: 20px 0;
			flex-direction: column;
			align-items: stretch;
			gap: 16px;
		}

		&__title {
			font-size: 24px;
			line-height: 34px;
		}

		&__filters {
			gap: 12px;
			flex-wrap: wrap;
		}

		&__list {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 14px;
		}

		&__footer {
			margin-top: 24px;
		}
	}

	.teacher-recommend-card {
		min-width: 0;

		&__image-box {
			height: 180px;
			padding: 0 12px;
		}

		&__content {
			padding: 14px 14px 16px;
		}

		&__name {
			font-size: 18px;
			height: auto;
			line-height: 26px;
		}

		&__title {
			font-size: 14px;
			line-height: 20px;
			height: auto;
			white-space: normal;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			line-clamp: 2;
			-webkit-box-orient: vertical;
		}

		&__price {
			font-size: 18px;
			line-height: 26px;
		}

		&__button {
			height: 42px;
			font-size: 17px;
			line-height: 42px;
		}
	}
}

@media (max-width: 480px) {
	.teacher-recommend-section {
		&__list {
			grid-template-columns: minmax(0, 1fr);
		}

		&__filters {
			width: 100%;

			.sort-select-wrapper {
				flex: 1;
				min-width: 0;

				.sort-select {
					width: 100%;
					min-width: 0;
				}
			}
		}
	}
}
</style>
